import React, { useCallback, useContext, useRef, useState } from "react";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { IntlContext, AuthContext } from "context";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { getErrorResponseData } from "helpers/typing";
import { Radio, TextField } from "components/Form/Field";
import Button from "ui/Button";
import { useRegisterStepper } from "../common/RegisterStepper";
import { useRegisterDraftGuard } from "../common/useRegisterDraftGuard";
import type { RegisterDraft } from "../types";
import { validateRegisterClientInformations } from "hooks/register";
import { Gender } from "types/user";
import Address from "components/Form/Address";
import { trackRegisterStepComplete } from "lib/analytics/dataLayer";

interface Props {
    client: RegisterDraft | undefined;
    setClient: (client: RegisterDraft) => void;
}

type DateInputWithPicker = HTMLInputElement & { showPicker?: () => void };

const RegisterClientInfos: React.FC<Props> = ({ client, setClient }) => {
    const intl = useContext(IntlContext);
    const auth = useContext(AuthContext);
    const [inputIsPassword, setInputIsPassword] = useState<boolean>(true);
    const stepper = useRegisterStepper();

    const sectionTitleClassName =
        "flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-foreground";
    const sectionIndexClassName =
        "inline-flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-primary font-black";
    const fieldLabelClassName = "text-[11px] font-bold uppercase tracking-wider text-muted-foreground";
    const fieldInputClassName =
        "field-control__input h-12 rounded-full border border-input bg-muted/50 px-5 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring";
    const passwordViewerClassName =
        "cursor-pointer absolute right-4 top-1/2 -translate-y-1/2";
    const todayIso = new Date().toISOString().slice(0, 10);

    const birthdayInputRef = useRef<DateInputWithPicker>(null);

    const openBirthdayPicker = useCallback(() => {
        const el = birthdayInputRef.current;
        if (!el) return;
        el.focus();
        el.showPicker?.();
    }, []);

    useRegisterDraftGuard(client, "prestations");

    const isNonEmptyString = (value: unknown) => String(value ?? "").trim().length > 0;

    const onSubmit = async (values: RegisterDraft) => {
        try {
            const data = await validateRegisterClientInformations(values);
            setClient(Object.assign({}, client, data));
            trackRegisterStepComplete({
                registerType: "client",
                stepKey: "informations",
                stepIndex: 3,
                stepLabel: intl.formatMessage("authentication.register.client_flow.informations.label"),
            });
            stepper.goTo("cesu");
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <Form
                onSubmit={onSubmit}
                initialValues={client}
                mutators={{ ...arrayMutators }}
                validate={(values: RegisterDraft) => {
                    const errors: Record<string, string> = {};

                    if (!auth.account?.user.roles) {
                        const password = String(values?.password ?? "");
                        const passwordConfirmation = String(values?.password_confirmation ?? "");

                        const hasMinLength = password.length >= 8;
                        const hasUppercase = /[A-Z]/.test(password);
                        const hasLowercase = /[a-z]/.test(password);
                        const hasDigit = /\d/.test(password);
                        const hasSpecial = /[^A-Za-z0-9]/.test(password);

                        if (password.trim() && (
                            !hasMinLength ||
                            !hasUppercase ||
                            !hasLowercase ||
                            !hasDigit ||
                            !hasSpecial
                        )) {
                            errors.password =
                                "Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.";
                        }

                        if (passwordConfirmation && passwordConfirmation !== password) {
                            errors.password_confirmation = "Les mots de passe ne correspondent pas.";
                        }
                    }

                    return errors;
                }}
                render={({ handleSubmit, submitting, values, hasValidationErrors }) => {
                    const canGoNext =
                        (isNonEmptyString(values?.email) &&
                            isNonEmptyString(values?.password) &&
                            isNonEmptyString(values?.password_confirmation) &&
                            isNonEmptyString(values?.gender) &&
                            isNonEmptyString(values?.firstname) &&
                            isNonEmptyString(values?.lastname) &&
                            isNonEmptyString(values?.phone) &&
                            isNonEmptyString(values?.birthday));

                    return (
                    <form onSubmit={handleSubmit} className="register__form">
                        <div className="flex flex--direction-column gap-10">
                            <p className="h1 font-black">
                                Parlons un peu <span className="block text-primary">de vous !</span>
                            </p>
                            <p className="h4 margin-bottom--2 margin-top--1">
                                Créez votre compte et complétez votre profil public pour publier votre annonce.
                            </p>

                            {!auth.account?.user.roles && (
                                <div className="register__group-fields">
                                    <div className="space-y-4">
                                        <div className={sectionTitleClassName}>
                                            <span className={sectionIndexClassName}>1</span>
                                            <span className="font-black">Mes identifiants</span>
                                        </div>

                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <TextField
                                                inputProps={{
                                                    name: "email",
                                                    required: true,
                                                    placeholder: "votre@email.com",
                                                    maxLength: 255,
                                                    className: fieldInputClassName,
                                                }}
                                                labelProps={{
                                                    label: intl.formatMessage("authentication.register.email"),
                                                    className: fieldLabelClassName,
                                                }}
                                            />
                                            <TextField
                                                inputProps={{
                                                    name: "password",
                                                    required: true,
                                                    type: inputIsPassword ? "password" : "text",
                                                    placeholder: "********",
                                                    maxLength: 255,
                                                    className: fieldInputClassName,
                                                }}
                                                labelProps={{
                                                    label: intl.formatMessage("authentication.register.password"),
                                                    className: fieldLabelClassName,
                                                }}
                                                adornmentEnd={
                                                    <div
                                                        onClick={() => setInputIsPassword(!inputIsPassword)}
                                                        className={passwordViewerClassName}
                                                    >
                                                        {inputIsPassword && (
                                                            <Icon icon={{ prefix: "fas", iconName: "eye-slash" }} />
                                                        )}
                                                        {!inputIsPassword && <Icon icon={{ prefix: "fas", iconName: "eye" }} />}
                                                    </div>
                                                }
                                            />
                                            <TextField
                                                inputProps={{
                                                    name: "password_confirmation",
                                                    required: true,
                                                    type: inputIsPassword ? "password" : "text",
                                                    placeholder: "********",
                                                    maxLength: 255,
                                                    className: fieldInputClassName,
                                                }}
                                                labelProps={{
                                                    label: intl.formatMessage("authentication.register.password_confirm"),
                                                    className: fieldLabelClassName,
                                                }}
                                                adornmentEnd={
                                                    <div
                                                        onClick={() => setInputIsPassword(!inputIsPassword)}
                                                        className={passwordViewerClassName}
                                                    >
                                                        {inputIsPassword && (
                                                            <Icon icon={{ prefix: "fas", iconName: "eye-slash" }} />
                                                        )}
                                                        {!inputIsPassword && <Icon icon={{ prefix: "fas", iconName: "eye" }} />}
                                                    </div>
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!auth.account?.user.roles && (
                                <div className="register__group-fields">
                                    <div className="space-y-4">
                                        <div className={sectionTitleClassName}>
                                            <span className={sectionIndexClassName}>2</span>
                                            <span className="font-black">Mon identité</span>
                                        </div>

                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div className="sm:col-span-2">
                                                <Radio
                                                    inputProps={{
                                                        name: "gender",
                                                        required: true,
                                                        className: "peer sr-only",
                                                        groupClassName: "w-full",
                                                        labelClassName:
                                                            "flex h-12 w-full cursor-pointer items-center justify-center rounded-full border bg-background text-sm font-bold transition border-input hover:border-primary/40 peer-checked:border-primary peer-checked:bg-primary/5 peer-checked:ring-2 peer-checked:ring-primary/20 peer-focus-visible:ring-2 peer-focus-visible:ring-ring",
                                                        options: [
                                                            { value: String(Gender.MALE), label: "M." },
                                                            { value: String(Gender.FEMALE), label: "Mme" },
                                                        ],
                                                    }}
                                                    fieldControlProps={{
                                                        className: "mt-2 grid grid-cols-2 gap-4 field-control-radio",
                                                    }}
                                                    labelProps={{
                                                        label: intl.formatMessage("authentication.register.label.gender"),
                                                        className: fieldLabelClassName,
                                                    }}
                                                />
                                            </div>
                                            <TextField
                                                inputProps={{
                                                    name: "firstname",
                                                    required: true,
                                                    placeholder: "ex: Isabelle",
                                                    maxLength: 255,
                                                    className: fieldInputClassName,
                                                }}
                                                labelProps={{
                                                    label: intl.formatMessage("authentication.register.label.firstname.user"),
                                                    className: fieldLabelClassName,
                                                }}
                                            />
                                            <TextField
                                                inputProps={{
                                                    name: "lastname",
                                                    required: true,
                                                    placeholder: "ex: Martin",
                                                    maxLength: 255,
                                                    className: fieldInputClassName,
                                                }}
                                                labelProps={{
                                                    label: intl.formatMessage("authentication.register.label.lastname.user"),
                                                    className: fieldLabelClassName,
                                                }}
                                            />

                                            <TextField
                                                inputProps={{
                                                    name: "phone",
                                                    required: true,
                                                    placeholder: "ex: 0612345678",
                                                    maxLength: 10,
                                                    pattern: "^[0-9]{10}$",
                                                    className: fieldInputClassName,
                                                }}
                                                labelProps={{
                                                    label: intl.formatMessage("authentication.register.label.phone"),
                                                    className: fieldLabelClassName,
                                                }}
                                            />

                                            <TextField
                                                inputProps={{
                                                    ref: birthdayInputRef,
                                                    name: "birthday",
                                                    type: "date",
                                                    max: todayIso,
                                                    required: true,
                                                    className: `${fieldInputClassName} date-input--placeholder pr-12`,
                                                }}
                                                labelProps={{
                                                    label: intl.formatMessage("authentication.register.label.birthday"),
                                                    className: fieldLabelClassName,
                                                }}
                                                adornmentEnd={
                                                    <button
                                                        type="button"
                                                        className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-accent hover:text-foreground"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={openBirthdayPicker}
                                                        aria-label="Open calendar"
                                                    >
                                                        <Calendar className="h-4 w-4" />
                                                    </button>
                                                }
                                            />

                                            <Address
                                                inputProps={{
                                                    name: "address_birth",
                                                    type: "municipality",
                                                    latitudeName: "coordinates_lat_birth",
                                                    longitudeName: "coordinates_lon_birth",
                                                    city: "city_birth",
                                                    commune_code: "commune_code_birth",
                                                    required: true,
                                                    placeholder: "ex: Nantes",
                                                }}
                                                labelProps={{
                                                    label: intl.formatMessage("authentication.register.label.city.birth"),
                                                    className: fieldLabelClassName,
                                                }}
                                            />

                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="flex items-center justify-between text-center margin-top--10">
                                <Button
                                    className="btn inline-flex items-center justify-center gap-3 rounded-full px-0 py-4 font-black text-muted-foreground/70"
                                    onClick={() => {
                                        setClient(Object.assign({}, client, values));
                                        stepper.goTo("need");
                                    }}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    {intl.formatMessage("generic.step.previous")}
                                </Button>
                                <Button
                                    className="btn btn--primary inline-flex items-center justify-center gap-3 rounded-full font-black px-10 py-4"
                                    type="submit"
                                    disabled={submitting || Boolean(hasValidationErrors) || !canGoNext}
                                >
                                    {intl.formatMessage("generic.step.next")}
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </form>
                    );
                }}
            />
        </React.Fragment>
    );
};

export default RegisterClientInfos;
